import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import { ArrowIcon } from '../../../assets/icons/index';
import { Company } from '../../../hooks/index';
import {
  NotificationAction,
  notificationActionTitle,
  renderMyHoldingButtonTypes,
  renderViewPayoutButtonTypes,
} from '../../../types/notification.types';
import { PageRoute } from '../../../types/page.types';
import { SubscriptionType } from '../../../types/plan.types';
import { AppFormattedMessage } from '../../app-formatted-message/index';
import Button from '../../button/index';
import { useIntervalFromToday } from '../format-date.util';

type NotificationProps = {
  createdAt: Date;
  dividendPayoutId?: string;
  handleCloseModal: () => void;
  isRead: boolean;
  message: string;
  onExercise: () => void;
  onInviteUser: () => void;
  onMarkAsRead: () => void;
  type?: NotificationAction;
};

export const ActivityNotification: FC<NotificationProps> = ({
  createdAt,
  dividendPayoutId,
  isRead,
  message,
  onInviteUser,
  onMarkAsRead,
  type,
  handleCloseModal,
  onExercise,
}) => {
  const navigate = useNavigate();
  const { selectedCompany } = Company.useSelected();
  const date = useIntervalFromToday(createdAt);
  const [read, setRead] = useState(false);
  const companySubscriptionType =
    selectedCompany?.latestSubscriptionData?.items?.data[0]?.price?.product?.metadata?.type;

  return (
    <div
      className={twMerge(
        'flex flex-col gap-4 rounded-lg border border-brand-50 bg-brand-25 px-4 py-3',
        (isRead || read) && 'border-gray-200 bg-white',
      )}
    >
      <div className="flex w-full justify-between text-label-md font-medium text-gray-500">
        <span>{type && <AppFormattedMessage id={notificationActionTitle[type]} />}</span>
        <span>{date}</span>
      </div>
      <div className="flex gap-3">
        <div className="size-10 shrink-0 rounded-full bg-brand-700" />
        <div className="flex flex-col gap-4">
          <span className="text-sm font-[450] text-black">{message}</span>
          <div className="flex items-center gap-4 max-lg:justify-center">
            {!(isRead || read) && (
              <>
                <Button
                  className="w-fit text-nowrap text-sm font-medium text-brand-700"
                  onClick={() => {
                    onMarkAsRead();
                    setRead(true);
                  }}
                  styleType="NONE"
                >
                  <AppFormattedMessage id="MARK_AS_READ" />
                </Button>
              </>
            )}
            {type === NotificationAction.STAKEHOLDER_ADDED && (
              <>
                <div className="size-[5px] rounded-full bg-gray-300" data-permission-required />

                <Button
                  className="h-[22px] w-fit gap-2 text-nowrap rounded-full border border-gray-200 bg-white px-2 !text-label-md font-medium leading-[18px] text-gray-700"
                  data-permission-required
                  onClick={onInviteUser}
                  styleType="NONE"
                >
                  <AppFormattedMessage id="INVITE_USER" />
                  <ArrowIcon className="rotate-180" />
                </Button>
              </>
            )}
            {type === NotificationAction.STAKEHOLDER_LIMIT_REACHED && (
              <>
                <div className="size-[5px] rounded-full bg-gray-300" data-permission-required />

                <Button
                  className="h-[22px] w-fit gap-2 text-nowrap rounded-full border border-gray-200 bg-white px-2 !text-label-md font-medium leading-[18px] text-gray-700"
                  data-permission-required
                  onClick={() => {
                    handleCloseModal();
                    companySubscriptionType === SubscriptionType.ENTERPRISE
                      ? (window.location.href = 'mailto:contact@capquest.io')
                      : navigate(PageRoute.COMPANY_SETTINGS);
                  }}
                  styleType="NONE"
                >
                  {companySubscriptionType === SubscriptionType.ENTERPRISE ? (
                    'Talk to Sales'
                  ) : (
                    <AppFormattedMessage id="UPGRADE" />
                  )}

                  <ArrowIcon className="rotate-180" />
                </Button>
              </>
            )}
            {type && renderMyHoldingButtonTypes.includes(type) && (
              <Button
                className="h-[22px] w-fit gap-2 text-nowrap rounded-full border border-gray-200 bg-white px-2 !text-label-md font-medium leading-[18px] text-gray-700"
                onClick={() => {
                  handleCloseModal();
                  navigate(PageRoute.MY_HOLDINGS);
                }}
                styleType="NONE"
              >
                <AppFormattedMessage id="MY_HOLDINGS" />
                <ArrowIcon className="rotate-180" />
              </Button>
            )}
            {type && renderViewPayoutButtonTypes.includes(type) && (
              <Button
                className="h-[22px] w-fit gap-2 text-nowrap rounded-full border border-gray-200 bg-white px-2 !text-label-md font-medium leading-[18px] text-gray-700"
                onClick={() => {
                  handleCloseModal();
                  navigate(
                    dividendPayoutId
                      ? `${PageRoute.DIVIDENDS}?payoutId=${dividendPayoutId}`
                      : PageRoute.DIVIDENDS,
                  );
                }}
                styleType="NONE"
              >
                <AppFormattedMessage id="VIEW_PAYOUT" />
                <ArrowIcon className="rotate-180" />
              </Button>
            )}
            {type === NotificationAction.FINAL_DAY_TO_EXERCISE_REACHED && (
              <div className="flex w-full justify-end" data-permission-required>
                <Button
                  className="w-fit px-4 py-2 text-sm font-[550] text-white"
                  onClick={onExercise}
                >
                  <AppFormattedMessage id="EXERCISE_RETURN_TO_POOL" />
                </Button>
              </div>
            )}
            {type === NotificationAction.WARRANT_PLAN_EXPIRATION_7_DAYS && (
              <div className="flex w-full justify-end" data-permission-required>
                <Button
                  className="w-fit px-4 py-2 text-sm font-[550] text-white"
                  onClick={onExercise}
                >
                  <AppFormattedMessage id="EXERCISE" />
                </Button>
              </div>
            )}
            {type === NotificationAction.WARRANT_EXPIRATION_DATE_REACHED && (
              <div className="flex w-full justify-end" data-permission-required>
                <Button
                  className="w-fit px-4 py-2 text-sm font-[550] text-white"
                  onClick={onExercise}
                >
                  <AppFormattedMessage id="EXPIRE" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
